import { httpService } from '../utils/http';
import { 
  ApiResponse, 
  Page, 
  DeviceType,
  DeviceTypeRequest,
  DeviceTypeQueryParams,
  DeviceBatch,
  DeviceBatchRequest,
  DeviceBatchQueryRequest,
  Device,
  DeviceRequest,
  DeviceQueryRequest,
  ImportResult,
} from '../types';

/**
 * 设备管理服务类
 * 提供设备类型、设备批次、设备管理相关的API接口调用
 */
export class DeviceService {
  // ==================== 设备类型管理 ====================

  /**
   * 创建设备类型
   * @param data 设备类型数据
   * @returns Promise<ApiResponse<DeviceType>>
   */
  async createDeviceType(data: DeviceTypeRequest): Promise<ApiResponse<DeviceType>> {
    return httpService.post('/device-type/create', data);
  }

  /**
   * 更新设备类型
   * @param id 设备类型ID
   * @param data 设备类型数据
   * @returns Promise<ApiResponse<DeviceType>>
   */
  async updateDeviceType(id: number, data: DeviceTypeRequest): Promise<ApiResponse<DeviceType>> {
    return httpService.post(`/device-type/${id}`, data);
  }

  /**
   * 根据ID获取设备类型
   * @param id 设备类型ID
   * @returns Promise<ApiResponse<DeviceType>>
   */
  async getDeviceTypeById(id: number): Promise<ApiResponse<DeviceType>> {
    return httpService.get(`/device-type/${id}`);
  }

  /**
   * 获取所有设备类型
   * @returns Promise<ApiResponse<DeviceType[]>>
   */
  async getAllDeviceTypes(): Promise<ApiResponse<DeviceType[]>> {
    return httpService.get('/device-type/all');
  }

  /**
   * 分页查询设备类型
   * @param params 查询参数
   * @returns Promise<ApiResponse<Page<DeviceType>>>
   */
  async getDeviceTypeList(params?: DeviceTypeQueryParams): Promise<ApiResponse<Page<DeviceType>>> {
    const queryParams = {
      pageNo: params?.pageNo || 1,
      pageSize: params?.pageSize || 10,
      sortField: params?.sortField || 'createdAt',
      sortDirection: params?.sortDirection || 'desc',
    };
    return httpService.get('/device-type', queryParams);
  }

  /**
   * 删除设备类型
   * @param id 设备类型ID
   * @returns Promise<ApiResponse<void>>
   */
  async deleteDeviceType(id: number): Promise<ApiResponse<void>> {
    return httpService.post(`/device-type/${id}/delete`);
  }

  // ==================== 设备批次管理 ====================

  /**
   * 创建设备批次
   * @param data 设备批次数据
   * @returns Promise<ApiResponse<DeviceBatch>>
   */
  async createDeviceBatch(data: DeviceBatchRequest): Promise<ApiResponse<DeviceBatch>> {
    return httpService.post('/device-batch/create', data);
  }

  /**
   * 更新设备批次
   * @param id 设备批次ID
   * @param data 设备批次数据
   * @returns Promise<ApiResponse<DeviceBatch>>
   */
  async updateDeviceBatch(id: number, data: DeviceBatchRequest): Promise<ApiResponse<DeviceBatch>> {
    return httpService.post(`/device-batch/${id}/update`, data);
  }

  /**
   * 根据ID获取设备批次
   * @param id 设备批次ID
   * @returns Promise<ApiResponse<DeviceBatch>>
   */
  async getDeviceBatchById(id: number): Promise<ApiResponse<DeviceBatch>> {
    return httpService.get(`/device-batch/${id}`);
  }

  /**
   * 分页查询设备批次
   * @param params 查询参数
   * @returns Promise<ApiResponse<Page<DeviceBatch>>>
   */
  async getDeviceBatchList(params: DeviceBatchQueryRequest): Promise<ApiResponse<Page<DeviceBatch>>> {
    return httpService.post('/device-batch/query', params);
  }

  /**
   * 删除设备批次
   * @param id 设备批次ID
   * @returns Promise<ApiResponse<void>>
   */
  async deleteDeviceBatch(id: number): Promise<ApiResponse<void>> {
    return httpService.post(`/device-batch/${id}/delete`);
  }

  /**
   * 实时更新设备批次数量
   * @param id 设备批次ID
   * @returns Promise<ApiResponse<number>>
   */
  async updateDeviceBatchCount(id: number): Promise<ApiResponse<number>> {
    return httpService.post(`/device-batch/${id}/update-count`);
  }

  // ==================== 设备管理 ====================

  /**
   * 创建设备
   * @param data 设备数据
   * @returns Promise<ApiResponse<Device>>
   */
  async createDevice(data: DeviceRequest): Promise<ApiResponse<Device>> {
    return httpService.post('/device/dto', data);
  }

  /**
   * 更新设备
   * @param id 设备ID
   * @param data 设备数据
   * @returns Promise<ApiResponse<Device>>
   */
  async updateDevice(id: number, data: DeviceRequest): Promise<ApiResponse<Device>> {
    return httpService.post(`/device/${id}/dto`, data);
  }

  /**
   * 根据ID获取设备
   * @param id 设备ID
   * @returns Promise<ApiResponse<Device>>
   */
  async getDeviceById(id: number): Promise<ApiResponse<Device>> {
    return httpService.get(`/device/${id}`);
  }

  /**
   * 根据设备编号获取设备
   * @param deviceNum 设备编号
   * @returns Promise<ApiResponse<Device>>
   */
  async getDeviceByNum(deviceNum: string): Promise<ApiResponse<Device>> {
    return httpService.get(`/device/num/${deviceNum}`);
  }

  /**
   * 分页查询设备
   * @param params 查询参数
   * @returns Promise<ApiResponse<Page<Device>>>
   */
  async getDeviceList(params: DeviceQueryRequest): Promise<ApiResponse<Page<Device>>> {
    return httpService.post('/device/query', params);
  }

  /**
   * 删除设备
   * @param id 设备ID
   * @returns Promise<ApiResponse<void>>
   */
  async deleteDevice(id: number): Promise<ApiResponse<void>> {
    return httpService.post(`/device/${id}/delete`);
  }

  /**
   * 批量删除设备
   * @param ids 设备ID数组
   * @returns Promise<ApiResponse<void>>
   */
  async batchDeleteDevices(ids: number[]): Promise<ApiResponse<void>> {
    return httpService.post('/device/batch-delete', ids);
  }

  /**
   * Excel导入设备
   * @param file Excel文件
   * @param typeId 设备类型ID
   * @param batchId 设备批次ID
   * @returns Promise<ApiResponse<ImportResult>>
   */
  async importDevices(file: File, typeId: number, batchId: number): Promise<ApiResponse<ImportResult>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('typeId', typeId.toString());
    formData.append('batchId', batchId.toString());

    const token = localStorage.getItem('accessToken') || '';
    const response = await fetch(`${(httpService as any).baseURL}/device/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Excel导出设备
   * @param params 查询参数（不需要分页参数）
   * @returns Promise<Blob>
   */
  async exportDevices(params: Omit<DeviceQueryRequest, 'pageNo' | 'pageSize'>): Promise<Blob> {
    const token = localStorage.getItem('accessToken') || '';
    const response = await fetch(`${(httpService as any).baseURL}/device/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }
}

// 创建默认实例
export const deviceService = new DeviceService();

// 导出默认实例
export default deviceService;

